import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "./Button";
import { Input } from "./Input";
import { Field } from "./Field";

export function LiveSaveSpot({ lakeId }: { lakeId: number | string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    distance_m: "",
    depth_ft: "",
    substrate: "",
    feature_description: "",
    private_notes: "",
  });

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!form.name.trim()) {
      setMessage("Give the spot a name");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(`/app-api/lakes/${lakeId}/spots`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          distance_m: form.distance_m || null,
          depth_ft: form.depth_ft || null,
          substrate: form.substrate || null,
          feature_description: form.feature_description || null,
          private_notes: form.private_notes || null,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not save spot");
      await queryClient.invalidateQueries({ queryKey: ["carpintel_saved_spots", Number(lakeId)] });
      setForm({ name: "", distance_m: "", depth_ft: "", substrate: "", feature_description: "", private_notes: "" });
      setOpen(false);
      setMessage("Spot saved and ready to use when logging a catch");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Could not save spot");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-lg border border-border bg-raised p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-primary">Saved spots</h2>
          <p className="mt-1 text-sm leading-5 text-secondary">
            Save where you are fishing. Rod tackle stays separate in Rod setups.
          </p>
        </div>
        {!open ? <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>Save spot</Button> : null}
      </div>
      {open ? (
        <form onSubmit={save} className="mt-4 space-y-3 border-t border-border pt-4">
          <Field label="Spot name" id="live-spot-name">
            <Input id="live-spot-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Far island margin" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Distance (m)" id="live-spot-distance">
              <Input id="live-spot-distance" type="number" min="0" step="0.5" value={form.distance_m} onChange={(e) => setForm((f) => ({ ...f, distance_m: e.target.value }))} />
            </Field>
            <Field label="Depth (ft)" id="live-spot-depth">
              <Input id="live-spot-depth" type="number" min="0" step="0.1" value={form.depth_ft} onChange={(e) => setForm((f) => ({ ...f, depth_ft: e.target.value }))} />
            </Field>
          </div>
          <Field label="Substrate" id="live-spot-substrate">
            <Input id="live-spot-substrate" value={form.substrate} onChange={(e) => setForm((f) => ({ ...f, substrate: e.target.value }))} placeholder="e.g. gravel, silt, clay" />
          </Field>
          <Field label="Feature" id="live-spot-feature">
            <Input id="live-spot-feature" value={form.feature_description} onChange={(e) => setForm((f) => ({ ...f, feature_description: e.target.value }))} placeholder="e.g. island margin, gravel bar" />
          </Field>
          <Field label="Private notes" id="live-spot-notes">
            <Input id="live-spot-notes" value={form.private_notes} onChange={(e) => setForm((f) => ({ ...f, private_notes: e.target.value }))} />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" size="sm" isLoading={saving}>Save spot</Button>
            <Button type="button" size="sm" variant="tertiary" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </form>
      ) : null}
      {message ? <p className="mt-3 text-sm text-secondary" role="status">{message}</p> : null}
    </section>
  );
}
